<script setup lang="tsx">
import type { PostgrestSingleResponse } from '@supabase/supabase-js';

import type { RoleRow } from '@/src/core/admin/roles';
import type { Json } from '@/supabase/types';

import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { useOrgs } from '@/src/core/admin/orgs';
import { useRoles } from '@/src/core/admin/roles';
import { useAppState } from '@/src/store/ocg-app-state';

import { supabase } from '../../supabase';
import { getMessage } from '../../utils/exception';

const emit = defineEmits<{
  error: [message: string];
}>();

const appState = useAppState();

const dialog = ref(false);
const dialogValid = ref<boolean | null>(null);
const editedIndex = ref<number | null>(null);

const dialogDelete = ref(false);

interface RolePermission {
  role_id: string;
  resource: string;
  permission: string;
}

interface ResourceRow {
  resource: string;
  type: string;
  meta: { [key: string]: Json | undefined };
}

interface ResourceTypeRow {
  type: string;
  permission: string;
  description: string;
  details: string;
}

interface Permission {
  resource: string;
  permission: string;
}

interface Data {
  id: string | null;
  orgId: string | null;
  name: string;
  description: string;
  permissions: Permission[];
}

const dialogTitle = computed(() => {
  return editedIndex.value === null ? 'New Role' : 'Edit Role';
});

const busy = ref(true);

const itemsPerPageOptions = [5, -1];

const { roles: roleItems, loadRoles } = useRoles();
const { orgs, loadOrgs, orgAutocompleteItems } = useOrgs();

const rolePermissions = ref<RolePermission[]>([]);
const resourceItems = ref<ResourceRow[]>([]);
const resourceTypeItems = ref<ResourceTypeRow[]>([]);

const protectedNames = ['admin', 'superadmin'];

const defaultItem = computed<Data>(() => ({
  id: null,
  orgId: orgs.value.length === 1 ? orgs.value[0].id : null,
  name: '',
  description: '',
  permissions: [],
}));
const editedItem = ref<Data>();

const resourcePermissionItems = computed(() => {
  return resourceItems.value
    .map((resource) => {
      return resourceTypeItems.value
        .filter(type => type.type === resource.type)
        .map((type) => {
          return {
            resource: resource.resource,
            type: resource.type,
            meta: resource.meta,
            permission: type.permission,
            description: type.description,
            details: type.details,
          };
        });
    })
    .flat();
});

type ResourcePermissionItem = (typeof resourcePermissionItems.value)[0];

const headers = ref([
  {
    title: 'Organization',
    align: 'start' as const,
    sortable: true,
    key: 'org_card.fn',
  },
  {
    title: 'Role',
    align: 'start' as const,
    sortable: true,
    key: 'description',
  },
  { title: 'Actions', key: 'actions', sortable: false },
]);

const rules = {
  required: (value: string) => !!value || 'Field is required',
  email: (value: string) =>
    /.[^\n\r@\u2028\u2029]*@.+\..{2,}/.test(value)
    || 'Please enter an email address',
};

async function loadRolePermissions() {
  const { data, error } = await supabase.from('role_permission').select();
  if (error) throw error;
  rolePermissions.value = data as RolePermission[];
}

async function loadResources() {
  const { data, error } = await supabase.from('resource').select();
  if (error) throw error;
  resourceItems.value = data as ResourceRow[];
}

async function loadResourceTypes() {
  const { data, error } = await supabase.from('resource_types').select();
  if (error) throw error;
  resourceTypeItems.value = data as ResourceTypeRow[];
}

// TODO should get this type from the TS definition
interface PromiseRejectedResult {
  status: 'rejected';
  reason: any;
}

function editedItemFromRow(item: RoleRow): Data {
  const permissions = rolePermissions.value
    .filter(p => p.role_id === item.id)
    .map((p) => {
      return {
        resource: p.resource,
        permission: p.permission,
      };
    });
  return {
    id: item.id,
    orgId: item.org,
    name: item.name,
    description: item.description,
    permissions,
  };
}

function editItem(item: RoleRow) {
  editedIndex.value = roleItems.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialog.value = true;
}

function resetEditedItem() {
  editedIndex.value = null;
  editedItem.value = Object.assign({} as Data, defaultItem.value);
}

function resourcePermissionChipLabel(
  item: ResourcePermissionItem | undefined,
): string {
  if (!item) return '';
  // currently assumes item.type === 'org'
  return `${item.meta.fn}: ${item.description}`;
}

watch(
  dialog,
  (value) => {
    if (!value) {
      nextTick(resetEditedItem);
    }
  },
  { immediate: true },
);

watch(dialogDelete, (value) => {
  if (!value) {
    nextTick(resetEditedItem);
  }
});

async function load() {
  busy.value = true;
  const results = await Promise.allSettled([
    loadResourceTypes(),
    loadResources(),
    loadRoles(),
    loadRolePermissions(),
    loadOrgs(),
  ]);
  busy.value = false;
  const errors = results
    .filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )
    .map(result => getMessage(result.reason))
    .join('\n');
  if (errors) {
    emit('error', errors);
  }
  resetEditedItem();
}

defineExpose({ load });

onMounted(load);

async function save() {
  if (!dialogValid.value) return;
  if (!editedItem.value) return;

  try {
    let editedId = editedItem.value.id;
    if (editedId) {
      // update
      const { count, error } = await supabase
        .from('role')
        .update(
          {
            role: editedItem.value.name,
            description: editedItem.value.description,
          },
          { count: 'exact' },
        )
        .eq('id', editedId);
      if (error) throw error;
      if (count === 0) throw new Error('Could not update role.');
    }
    else {
      // create
      const { data, error } = await supabase
        .from('role')
        .insert({
          org: editedItem.value.orgId,
          role: editedItem.value.name,
          description: editedItem.value.description,
        })
        .select();
      if (error) throw error;
      editedId = data[0].id;
      editedItem.value.id = editedId;
    }
    const requests: Array<PromiseLike<PostgrestSingleResponse<any>>> = [];
    const currentPermissions = rolePermissions.value.filter(
      p => p.role_id === editedId,
    );
    editedItem.value.permissions.forEach((p) => {
      const existingIndex = currentPermissions.findIndex(
        cp => cp.resource === p.resource && cp.permission === p.permission,
      );
      if (existingIndex !== -1) {
        currentPermissions.splice(existingIndex, 1);
      }
      else {
        requests.push(
          supabase.from('role_permission').insert({
            role_id: editedId,
            resource: p.resource,
            permission: p.permission,
          }),
        );
      }
    });
    currentPermissions.forEach((p) => {
      requests.push(
        supabase
          .from('role_permission')
          .delete()
          .eq('role_id', editedId!)
          .eq('resource', p.resource)
          .eq('permission', p.permission),
      );
    });
    const responses = await Promise.all(requests);
    const errors = responses
      .filter(r => r.error)
      .map(r => getMessage(r.error))
      .join('\n');
    if (errors) throw errors;
  }
  catch (error) {
    console.error(getMessage(error));
    emit('error', getMessage(error));
  }
  finally {
    load();
    dialog.value = false;
  }
}

function deleteItem(item: RoleRow) {
  editedIndex.value = roleItems.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialogDelete.value = true;
}

async function deleteItemConfirm() {
  if (!editedItem.value?.id) return;
  try {
    const { count, error } = await supabase
      .from('role')
      .delete({ count: 'exact' })
      .eq('id', editedItem.value.id);
    if (error) throw error;
    if (count === 0) throw new Error('Could not delete role.');
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialogDelete.value = false;
  }
}
</script>

<template>
  <v-data-table
    items-per-page="5"
    :items-per-page-options="itemsPerPageOptions"
    :items="roleItems"
    :headers="headers"
    :loading="busy"
  >
    <template #top>
      <v-toolbar flat>
        <v-dialog v-model="dialog" max-width="50em">
          <template #activator="{ props }">
            <v-btn color="primary" v-bind="props" prepend-icon="mdi-plus">
              Add
            </v-btn>
            <v-spacer />
          </template>
          <v-card>
            <v-card-title>
              <span class="text-headline-small">{{ dialogTitle }}</span>
            </v-card-title>

            <v-form
              v-model="dialogValid"
              validate-on="invalid-input"
              @submit.prevent="save"
            >
              <v-card-text>
                <v-container v-if="editedItem">
                  <v-row>
                    <v-col
                      v-tooltip="{
                        disabled: !protectedNames.includes(editedItem.name),
                        text: 'Name cannot be changed',
                      }"
                      cols="12"
                      md="6"
                      sm="12"
                    >
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="[rules.required]"
                        label="Name"
                        :disabled="protectedNames.includes(editedItem.name)"
                      />
                    </v-col>
                    <v-col
                      v-tooltip="{
                        disabled: editedIndex === null,
                        text: 'Organization cannot be changed',
                      }"
                      cols="12"
                      md="6"
                      sm="12"
                    >
                      <v-autocomplete
                        v-model="editedItem.orgId"
                        label="Organization"
                        :items="orgAutocompleteItems"
                        :disabled="editedIndex !== null"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editedItem.description"
                        :rules="[rules.required]"
                        label="Description"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete
                        v-model="editedItem.permissions"
                        chips
                        label="Permissions"
                        :items="
                          resourcePermissionItems.map((permission) => ({
                            title: resourcePermissionChipLabel(permission),
                            props: { subtitle: `(${permission.details})` },
                            value: {
                              resource: permission.resource,
                              permission: permission.permission,
                            },
                          }))
                        "
                        multiple
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  @click="dialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  type="submit"
                  :disabled="dialogValid === false"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="dialogDelete"
          max-width="50em"
          @keydown.esc="dialogDelete = false"
        >
          <v-card>
            <v-card-title class="text-title-large text-center">
              Confirmation required
            </v-card-title>
            <v-card-text v-if="editedItem">
              <v-container class="d-flex flex-column">
                <v-row class="text-title-large text-center">
                  <v-col cols="12">
                    Really remove role
                    <span class="text-primary">{{ editedItem.name }}</span>?
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    All users will be removed from the deleted role.
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    This action <em>cannot</em> be undone and the role will be
                    <em>permanently deleted</em>.
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="dialogDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="deleteItemConfirm"
              >
                OK
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn
          :disabled="busy"
          icon="mdi-refresh"
          aria-label="Refresh"
          color="primary"
          @click="load"
        />
      </v-toolbar>
    </template>
    <template #item.actions="{ item }">
      <v-icon class="me-2" size="small" @click="editItem(item)">
        mdi-pencil
      </v-icon>
      <v-icon
        v-if="
          item.name !== 'admin' || appState.currentUserMode === 'superadmin'
        "
        size="small"
        @click="deleteItem(item)"
      >
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<style scoped></style>
